import React, { useCallback, useState } from 'react';
import { StyleSheet, TouchableWithoutFeedback, View } from 'react-native';
import { format, isSameDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import Text from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import Button from 'design-system/Button';
import { rem } from 'design-system/values';
import { TextButton } from 'design-system/TextButton';
import { Day } from 'design-system/calendar/Day';
import { Calendar } from 'design-system/calendar/Calendar';
import { isWeb } from '../../../../constants';
const SelectDateModalContent = ({ data, predictedDate, onFinish, selectExtraDays = false, disablePastDates = false, closeOnRevert = false, }) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    // Date strings are 'yyyy-MM-dd', so lexicographic comparison is chronological.
    const isPastDate = useCallback((dateString) => disablePastDates && dateString < today, [disablePastDates, today]);
    const [selected, setSelected] = useState(data);
    const [visibleMonth, setVisibleMonth] = useState(() => {
        const dateString = format(data, 'yyyy-MM-dd');
        return isPastDate(dateString) ? today : dateString;
    });
    // Bumped only when reverting, to force the calendar to jump to the
    // predicted month even if the user had navigated away.
    const [calendarKey, setCalendarKey] = useState(0);
    const onDayPress = useCallback((date) => {
        setSelected(toZonedTime(date.timestamp, 'UTC'));
    }, []);
    // Tapping a day from an adjacent month selects it and navigates the
    // calendar to that month.
    const onPressExtraDay = useCallback((date) => {
        setSelected(toZonedTime(date.timestamp, 'UTC'));
        setVisibleMonth(date.dateString);
        setCalendarKey((key) => key + 1);
    }, []);
    const revertToPredicted = useCallback(() => {
        if (!predictedDate)
            return;
        if (closeOnRevert) {
            onFinish(predictedDate);
            return;
        }
        setSelected(predictedDate);
        setVisibleMonth(format(predictedDate, 'yyyy-MM-dd'));
        setCalendarKey((key) => key + 1);
    }, [predictedDate, closeOnRevert, onFinish]);
    const save = useCallback(() => {
        onFinish(selected);
    }, [onFinish, selected]);
    // Compared against the prediction, not `data`: the modal can be reopened on
    // an already-changed date, and the revert control needs to stay available
    // whenever the selection differs from what was predicted.
    const hasChangedDate = predictedDate
        ? !isSameDay(selected, predictedDate)
        : false;
    return (<View>
      {!isWeb ? (<Text Text-24 style={styles.title}>
          Select a date
        </Text>) : (<Spacer h={16}/>)}

      <Calendar key={calendarKey} noMarginHorizontal noPaddingHorizontal unrestrictedWidth blackMonthText fixedHeight hideExtraDays={false} initialDate={visibleMonth} dayComponent={({ date, state }) => {
            if (!date) {
                return null;
            }
            // Days from the adjacent months are surfaced faintly and aren't
            // selectable.
            if (state === 'disabled') {
                const extraDay = (<View style={styles.extraDay}>
                <Text Numbers-14 Gray-50>
                  {date.day}
                </Text>
              </View>);
                return selectExtraDays && !isPastDate(date.dateString) ? (<TouchableWithoutFeedback onPress={() => onPressExtraDay(date)}>
                {extraDay}
              </TouchableWithoutFeedback>) : (extraDay);
            }
            return (<Day key={date.dateString} date={date} isDisabled={isPastDate(date.dateString)} isStartDay={isSameDay(selected, toZonedTime(date.timestamp, 'UTC'))} onDayPress={onDayPress}/>);
        }}/>
      {predictedDate ? (<TextButton text={hasChangedDate ? 'Revert date to predicted' : ' '} onPress={revertToPredicted} disabled={!hasChangedDate} style={styles.revert}/>) : (<Spacer h={32}/>)}
      <Button brand title="Save" onPress={save} style={styles.bottomMargin}/>
    </View>);
};
const styles = StyleSheet.create({
    title: {
        marginTop: rem(40),
        marginBottom: rem(16),
    },
    extraDay: {
        width: rem(38.5),
        height: rem(38.5),
        alignItems: 'center',
        justifyContent: 'center',
    },
    revert: {
        alignSelf: 'center',
        marginTop: rem(-8),
        marginBottom: rem(16),
    },
    bottomMargin: {
        marginBottom: rem(16),
    },
});
export default SelectDateModalContent;
